import { SELF_PAY_PAYER_ID } from '../config/supported-payers.js';

import { getAttribution, type Attribution } from './attribution/attribution.js';
import { ensureFunnelId } from './funnel/funnel-id.js';

/** Spread into funnel request bodies; absent attribution leaves the key out entirely. */
export function attributionField(): { attribution?: Attribution } {
  const attribution = getAttribution();
  return attribution !== undefined ? { attribution } : {};
}

/** Spread into funnel request bodies for every visitor, attributed or not; absent only if unmintable. */
export function funnelIdField(): { funnelId?: string } {
  const funnelId = ensureFunnelId();
  return funnelId !== undefined ? { funnelId } : {};
}

export interface EligibilityCheckInput {
  readonly payerId: string;
  /** BCNC-group-or-self-pay identifier (Task 5). Never sent to the `.strict()` eligibility-check
   *  schema — carried in app state for the outcome copy and sent on enrollment instead. */
  readonly groupId: string;
  /** Absent for self-pay: there is no member id to collect. */
  readonly memberId?: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly dateOfBirth: string; // YYYY-MM-DD
  /** Collected at signup for enrollment + Zoom prefill; also sent on the eligibility check
   *  (forwarded server-side to the cross-program member check fan). */
  readonly email: string;
  /**
   * 10 digits, already normalized by the form. Sent on enrollment (stored on the CareBetty
   * record) and used for the Zoom prefill — deliberately NOT sent on the eligibility check,
   * since a phone is not part of an X12 270 and that path has no use for the identifier.
   */
  readonly phone: string;
}

export interface EligibleResult {
  readonly kind: 'eligible';
  readonly eligible: boolean;
  readonly benefitCovered: boolean;
  readonly copayRequired: boolean;
  readonly copayAmountCents?: number;
  readonly costPath: 'covered' | 'copay' | 'out-of-pocket';
  /**
   * Same $100 initial-consult price a chosen self-pay outcome carries. Present only when
   * `costPath` is `'out-of-pocket'` — covered/copay members owe nothing or a copay, not this.
   * Server-owned (Task 2/3): the app must never fall back to a literal.
   */
  readonly selfPayPriceCents?: number;
}

export type EligibilityCheckResult =
  | EligibleResult
  | {
      kind: 'payer_unavailable';
      retryable: boolean;
      canProceedOutOfPocket: boolean;
      message: string;
    }
  | {
      kind: 'member_not_found';
      message: string;
      /** The member-not-found screen offers self-pay too; server-owned, same rule as above. */
      selfPayPriceCents?: number;
    }
  | { kind: 'existing_member' }
  | { kind: 'error'; message: string }
  /**
   * The member chose to self-pay instead of using insurance — no payer was checked, so this
   * carries no coverage claim, only the price (server-owned, same rule as above).
   * `selfPayPriceCents` is optional here (not `number`, matching the wire contract) even though
   * the server always sends it today: if a malformed response ever omitted it, defaulting to `0`
   * would quote a member a wrong price, which is exactly what "one owner" exists to prevent.
   * Absent ⇒ the component renders its no-figure fallback sentence instead.
   */
  | { kind: 'self_pay'; selfPayPriceCents?: number };

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * Base URL of the ffs-signup service.
 *
 * Deliberately NOT `VITE_SERVICES_BASE_URL`. That name means "the shared gateway"
 * (`https://services.<apex>`) and is set once for the whole deploy job, so every app reading it
 * gets the gateway. This service is standalone — `exposeViaSharedGateway: false`, its own HttpApi
 * with its own CORS — and is NOT routed through the gateway. Reading the shared name baked the
 * gateway host into this bundle, and the browser reported it as a CORS failure: the preflight hit
 * a gateway with no such route, which answers 204 with no `access-control-*` headers.
 *
 * Its own name, resolved from the service's SSM url param at build time, makes that collision
 * impossible.
 */
export function baseUrl(): string {
  // Static property access, not a dynamic index: Vite only substitutes `import.meta.env.NAME` at
  // transform time. The previous `env['NAME']` form read an inlined literal instead, which is also
  // why the value could not be injected in tests.
  return import.meta.env.VITE_FFS_SIGNUP_API_URL ?? '';
}

export async function checkEligibility(
  input: EligibilityCheckInput,
): Promise<EligibilityCheckResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/eligibility/check`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        payerId: input.payerId,
        // groupId deliberately omitted: the server's eligibility-check schema is `.strict()` and
        // would 400 on an unrecognized key. It carries through app state instead (outcome copy,
        // enrollment).
        ...(input.memberId !== undefined ? { memberId: input.memberId } : {}),
        firstName: input.firstName,
        lastName: input.lastName,
        dateOfBirth: input.dateOfBirth,
        email: input.email,
        ...attributionField(),
        ...funnelIdField(),
      }),
    });
  } catch {
    // Do NOT include `input` (PHI) in the message.
    return { kind: 'error', message: GENERIC_ERROR };
  }

  const body: unknown = await res.json().catch(() => ({}));
  if (res.ok) {
    // Any 2xx is a completed check (the NestJS POST returns 200; be tolerant of 201 too).
    // The server returns both outcomes at the same 2xx status, discriminated by `kind`: a
    // cross-program hit short-circuits enrollment and takes precedence over the Stedi result.
    const b = body as { kind?: string };
    if (b.kind === 'existing_member') {
      return { kind: 'existing_member' };
    }
    if (b.kind === 'self_pay') {
      const s = body as { selfPayPriceCents?: number };
      // `0` is not a safe default for a price quoted to a member — absent when the server omits
      // it, never a fabricated figure. The component's no-figure sentence handles this case.
      return {
        kind: 'self_pay',
        ...(s.selfPayPriceCents !== undefined ? { selfPayPriceCents: s.selfPayPriceCents } : {}),
      };
    }
    const o = body as Omit<EligibleResult, 'kind'>;
    return { kind: 'eligible', ...o };
  }
  if (res.status === 503) {
    const b = body as { retryable?: boolean; canProceedOutOfPocket?: boolean; message?: string };
    return {
      kind: 'payer_unavailable',
      retryable: b.retryable ?? true,
      canProceedOutOfPocket: b.canProceedOutOfPocket ?? true,
      message: b.message ?? GENERIC_ERROR,
    };
  }
  if (res.status === 422) {
    const b = body as { message?: string; selfPayPriceCents?: number };
    return {
      kind: 'member_not_found',
      message: b.message ?? GENERIC_ERROR,
      ...(b.selfPayPriceCents !== undefined ? { selfPayPriceCents: b.selfPayPriceCents } : {}),
    };
  }
  return { kind: 'error', message: GENERIC_ERROR };
}

export type EnrollmentResult =
  | { readonly kind: 'enrolled'; readonly bookingUrl: string; readonly patientId?: string }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'booking_unavailable'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

/** Enroll the eligible member (POST /v1/enrollment/route). Email is sent in the body over HTTPS. */
export async function routeEnrollment(input: EligibilityCheckInput): Promise<EnrollmentResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/enrollment/route`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        memberId: input.memberId,
        payerId: input.payerId,
        // Self-pay has no plan group to record — omitted entirely (not sent as an empty/self-pay
        // sentinel value) so the enrollment use case leaves PLAN-GROUP off the Observation.
        ...(input.payerId !== SELF_PAY_PAYER_ID ? { groupId: input.groupId } : {}),
        firstName: input.firstName,
        lastName: input.lastName,
        dateOfBirth: input.dateOfBirth,
        email: input.email,
        phone: input.phone,
        ...attributionField(),
        ...funnelIdField(),
      }),
    });
  } catch {
    // Do NOT include `input` (PHI) in the message.
    return { kind: 'error', message: GENERIC_ERROR };
  }

  return enrollmentResultFrom(res);
}

/** The member-facing outcome of a `POST /v1/enrollment/route` response. Shared by v1 and v2. */
export async function enrollmentResultFrom(res: Response): Promise<EnrollmentResult> {
  const body: unknown = await res.json().catch(() => ({}));
  if (res.ok) {
    // Any 2xx (NestJS POST returns 200; tolerate 201) carries the routing decision.
    const b = body as {
      status?: string;
      bookingUrl?: string;
      message?: string;
      patientId?: string;
    };
    if (b.status === 'routed' && b.bookingUrl) {
      return {
        kind: 'enrolled',
        bookingUrl: b.bookingUrl,
        ...(b.patientId ? { patientId: b.patientId } : {}),
      };
    }
    if (b.status === 'blocked_active_member') {
      return { kind: 'blocked', message: b.message ?? GENERIC_ERROR };
    }
    // `blocked_existing_member` (server-side cross-program re-derivation, POST
    // /v1/enrollment/route) and `blocked_active_member` (already-active FFS member) are distinct
    // on the wire — different backend rule, different failure mode — but converge to the same
    // member-facing "we already know you" treatment, so both map to kind: 'blocked'.
    if (b.status === 'blocked_existing_member') {
      return { kind: 'blocked', message: b.message ?? GENERIC_ERROR };
    }
    if (b.status === 'booking_unavailable') {
      return { kind: 'booking_unavailable', message: b.message ?? GENERIC_ERROR };
    }
  }
  return { kind: 'error', message: GENERIC_ERROR };
}

/** True when the member owes money: a copay, full out-of-pocket, or a chosen self-pay consult. */
export function requiresPayment(result: EligibilityCheckResult): boolean {
  if (result.kind === 'self_pay') return true;
  return (
    result.kind === 'eligible' &&
    (result.costPath === 'copay' || result.costPath === 'out-of-pocket')
  );
}

/** Create an Inbox Health setup intent for card capture (POST /v1/payment/setup-intent). */
export async function createSetupIntent(input: {
  patientId: string;
}): Promise<{ inboxHealthIntentId: string; clientSecret: string }> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/payment/setup-intent`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ patientId: input.patientId }),
    });
  } catch {
    throw new Error(GENERIC_ERROR);
  }

  const body: unknown = await res.json().catch(() => ({}));
  if (res.ok) {
    const b = body as { inboxHealthIntentId?: string; clientSecret?: string };
    if (b.inboxHealthIntentId && b.clientSecret) {
      return { inboxHealthIntentId: b.inboxHealthIntentId, clientSecret: b.clientSecret };
    }
  }
  throw new Error(GENERIC_ERROR);
}

/** Confirm the setup intent completed (POST /v1/payment/setup-intent/finalize). */
export async function finalizeSetupIntent(input: {
  inboxHealthIntentId: string;
  patientId: string;
}): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/payment/setup-intent/finalize`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        inboxHealthIntentId: input.inboxHealthIntentId,
        patientId: input.patientId,
      }),
    });
  } catch {
    throw new Error(GENERIC_ERROR);
  }

  if (!res.ok) {
    throw new Error(GENERIC_ERROR);
  }
}

/**
 * Notify our backend that the member completed Zoom booking (from the iframe postMessage).
 * Fire-and-forget correlation: the member HAS booked (Zoom + the server-side Zoom→CareBetty link
 * are the source of truth), so a failure here must not block the confirmation UI. Payload is
 * opaque IDs plus the non-PHI ad attribution and funnel id — never email/name/PHI.
 */
export async function notifyConsultBooked(input: {
  scheduledEventId: string;
  attendeeId: string;
  patientId?: string;
}): Promise<void> {
  try {
    await fetch(`${baseUrl()}/v1/consult/booked`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...input, ...attributionField(), ...funnelIdField() }),
    });
  } catch {
    // best-effort; swallow (no PHI in logs/messages)
  }
}
